import { MagnifyingGlassIcon } from "@radix-ui/react-icons";
import { useFormik } from "formik";
import { Filter } from "lucide-react";
import React, { useEffect, useState } from "react";
import CreateRecord from "./CreateRecord";
import NoInfo from "../NoInfo";
import RecordRow from "./RecordRow";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";
import { crmRecordKeyValue } from "../../../cf";
import Image from "../../Tags/Image";
import { useStoreIntegrations } from "../../../stores/useStoreIntegrations";

export default function EditMatch({
  closeModal,
  filters = {},
  submitFilter,
  isLoading = true,
  records = [],
  matchRecord,
  createRecordData = {},
  fetchedRecordObj = {},
  fetchedReferences = [],
  allRecords = [],
  objectWithSearchFilters = {},
  objectApiName = {},
  type = "",
  recordCreated,
  createDirect = false,
  resetFetchedRecordObj,
  crmObjectsConfig = [],
  contactRevealed = true,
  source = "",
}) {
  const { activeCRMIntegration } = useStoreIntegrations();
  const captureEvent = useCaptureEvent();
  const [toggleFilter, setToggleFilter] = useState(false);
  const handleToggleFilter = () => {
    setToggleFilter(!toggleFilter);
  };

  const [initialValues, setInitialValues] = useState({});

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    onSubmit: (values) => {
      submitFilter(values);
      setToggleFilter(false);
    },
  });

  useEffect(() => {
    if (fetchedRecordObj && Object.keys(fetchedRecordObj).length > 0) {
      const updatedInitialValues = Object.keys(fetchedRecordObj).reduce((acc, key) => {
        const fieldData = fetchedRecordObj[key]; // Get the inner object
        const fieldName = key || ""; // Ensure inner object exists
        acc[fieldName] = fieldData ? fieldData?.value : ""; // Get the value, fallback to an empty string
        return acc;
      }, {});
      setInitialValues(updatedInitialValues); // Set initial values once object is populated
    }
  }, [fetchedRecordObj]); // Re-run the effect when fetchedRecordObj changes

  const [create, setCreate] = useState(createDirect);

  // get crm object list
  const [personsWithWritePermission, setPersonsWithWritePermission] = useState([]);
  const [companyWithWritePermission, setCompanyWithWritePermission] = useState([]);
  const [persons, setPersons] = useState([]);

  const [crmConfigCompany, setCRMConfigCompany] = useState([]);
  const [crmConfigCompanyForRecordView, setCRMConfigCompanyForRecordView] = useState([]);
  const processSavedCRMConfig = () => {
    if (crmObjectsConfig && crmObjectsConfig.length > 0) {
      if (type === "person") {
        const arr = crmObjectsConfig.filter((obj) => obj.type === "person");
        const arr2 = crmObjectsConfig.filter(
          (obj) => obj.type === "person" && obj.permission?.create === true
        );
        setPersons(arr);
        setPersonsWithWritePermission(arr2);
        setCompanyWithWritePermission([]);
      }
      if (type === "company") {
        const arr_c = crmObjectsConfig.filter(
          (obj) => obj.type === "company" && obj.permission?.create === true
        );
        setCompanyWithWritePermission(arr_c);
        setPersons([]);
        setPersonsWithWritePermission([]);
      }
      try {
        const arr_c = crmObjectsConfig.filter(
          (obj) => obj.type === "company" && obj.permission?.create === true
        );
        const arr_c2 = crmObjectsConfig.filter((obj) => obj.type === "company");
        setCRMConfigCompany(arr_c);
        setCRMConfigCompanyForRecordView(arr_c2);
      } catch (err) {
        console.log(err);
      }
    }
  };

  useEffect(() => {
    if (crmObjectsConfig && crmObjectsConfig.length > 0) {
      processSavedCRMConfig();
    }
  }, [crmObjectsConfig?.length]);

  const [currentApiName, setCurrentApiName] = useState(objectApiName);
  useEffect(() => {
    if (currentApiName) {
      resetFetchedRecordObj(currentApiName);
    }
  }, [currentApiName, isLoading]);

  const returnTrue = (key) => {
    if (currentApiName === key) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <>
      {create ? (
        <CreateRecord
          cancel={() => {
            setInitialValues({});
            if (createDirect) {
              closeModal();
            } else {
              setCreate(false);
            }
          }}
          closeModal={closeModal}
          infoData={createRecordData}
          type={type}
          recordCreated={recordCreated}
          persons={personsWithWritePermission}
          objectApiName={
            type === "company"
              ? createRecordData?.crm_object_api_name
              : personsWithWritePermission && personsWithWritePermission.length > 0
                ? personsWithWritePermission[0]?.crm_object_api_name
                : ""
          }
          objectApiLabel={
            type === "company"
              ? createRecordData?.crm_object_api_label
              : personsWithWritePermission && personsWithWritePermission.length > 0
                ? personsWithWritePermission[0]?.crm_object_label
                : ""
          }
          crmConfigCompany={crmConfigCompany}
          crmConfigCompanyForRecordView={crmConfigCompanyForRecordView}
          contactRevealed={contactRevealed}
        />
      ) : (
        <div className=" p-6">
          <div className="bg-gray-50 -mx-6 p-6 border-b border-border -mt-6 mb-4">
            {source !== "app" && (
              <span
                className="flex items-center cursor-pointer mb-4 text-sm space-x-2"
                onClick={() => {
                  setInitialValues({});
                  closeModal();
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2.5 7.49996H13.75C15.8211 7.49996 17.5 9.17889 17.5 11.25C17.5 13.321 15.8211 15 13.75 15H10M2.5 7.49996L5.83333 4.16663M2.5 7.49996L5.83333 10.8333"
                    stroke="var(--gray-600)"
                    stroke-width="1.66667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <span>Back</span>
              </span>
            )}
            <div className="flex items-start space-x-2">
              <div>
                <p className="text-sm font-semibold text-gray-900">Choose matching CRM record</p>
                <p className="text-sm mt-2">
                  Please choose the correct matching record or create a new record.
                </p>
              </div>
            </div>
          </div>

          <div className="px-4 py-4 border border-input bg-background rounded-xl mb-4">
            <div
              className={`flex cursor-pointer ${toggleFilter && "border-b pb-4"}`}
              onClick={() => {
                handleToggleFilter();
                captureEvent("ECRM_EditMatch_SearchFiltersBtn", {
                  type: type,
                  apiName: currentApiName,
                });
              }}
            >
              <div className="inline-flex items-center space-x-2">
                <MagnifyingGlassIcon width={17} height={17} />{" "}
                <span className="text-sm font-semibold">Search filters</span>
              </div>
              <span className="border bg-background border-brand-300 text-brand-700 rounded-3xl px-2 text-[12px] ml-3">
                {Object.keys(fetchedRecordObj).length}
              </span>
            </div>
            {toggleFilter && (
              <>
                {persons && persons.length > 0 && (
                  <div className="my-4">
                    {persons.map((person) => (
                      <div className="mb-1 flex items-center space-x-3 text-sm">
                        <input
                          type="radio"
                          name="person"
                          value={person?.crm_object_api_name}
                          defaultChecked={returnTrue(person?.crm_object_api_name)}
                          onChange={(e) => {
                            console.log(e);
                            resetFetchedRecordObj(person?.crm_object_api_name);
                            setCurrentApiName(person?.crm_object_api_name);
                            captureEvent("ECRM_EditMatch_RadioBtnClick", {
                              type: type,
                              apiName: person?.crm_object_api_name,
                            });
                          }}
                        />{" "}
                        <span>{person?.crm_object_label}</span>
                      </div>
                    ))}
                  </div>
                )}
                <form onSubmit={formik.handleSubmit} className="mt-4 animate__animated animate__fadeIn">
                  {Object.keys(fetchedRecordObj).map((key, index) => {
                    const inputName = key || ""; // Ensure the inner object exists

                    return (
                      <div className="mb-3" key={index}>
                        <p className="mb-2 text-text-700 text-sm">{fetchedRecordObj[key]?.label}</p>
                        <input
                          type="text"
                          name={inputName}
                          className="rounded-[8px] w-full bg-background border border-gray-300 text-gray-900 text-[14px] p-2 h-10"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values[inputName] || ""} // Provide a fallback empty string
                        />
                      </div>
                    );
                  })}

                  <div className="flex items-center mt-4">
                    <button
                      disabled={isLoading}
                      type="submit"
                      className="text-[14px] disabled:opacity-70 flex w-6/12 justify-center items-center py-2 h-9 space-x-2 px-4 rounded-[8px] text-white bg-brand-600 dark:bg-brand-300 border border-brand-600 "
                      onClick={() => {
                        captureEvent("ECRM_EditMatch_SearchBtn", {
                          type: type,
                          apiName: currentApiName,
                        });
                      }}
                    >
                      <MagnifyingGlassIcon width={18} height={18} className="mr-1" />
                      Search
                    </button>
                    <span
                      onClick={() => {
                        setToggleFilter(false);
                        captureEvent("ECRM_EditMatch_CancelSearchBtn", {
                          type: type,
                          apiName: currentApiName,
                        });
                      }}
                      className="cursor-pointer inline-flex justify-center items-center whitespace-nowrap text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border bg-transparent border-transparent text-gray-600 px-4 py-2 h-9 rounded-[8px]"
                    >
                      Cancel
                    </span>
                  </div>
                </form>
              </>
            )}
          </div>

          {isLoading ? (
            <div className="flex justify-center flex-col items-center space-x-2 space-y-4 py-8">
              <div className="loader_circle loader_purple"></div>
              <span className="text-sm">Searching CRM records...</span>
            </div>
          ) : (
            <>
              {" "}
              {allRecords && allRecords.length > 0 ? (
                allRecords.map((item) => (
                  <div className="p-4 border border-input bg-gray-50 rounded-xl mt-4">
                    <span className="flex w-full items-center justify-between space-x-2 mb-3">
                      <span className="inline-flex items-center space-x-2">
                        {" "}
                        {item?.match_confirmed ? (
                          <Image
                            src={`/images/crm/${activeCRMIntegration?.integration?.key}-blue.svg`}
                            className="h-4 no-invert"
                          />
                        ) : (
                          <Image
                            src={`/images/crm/${activeCRMIntegration?.integration?.key}-gray.svg`}
                            className="h-4 no-invert"
                          />
                        )}
                        <span className="text-gray-900 text-sm font-semibold">
                          {item?.Name ? (
                            item?.Name
                          ) : (
                            <>
                              {item?.data?.name?.value
                                ? item?.data?.name?.value
                                : `${item?.data?.first_name?.value} ${item?.data?.last_name?.value}`}
                            </>
                          )}
                        </span>
                      </span>
                    </span>
                    <div className="border-b border-border my-4"></div>
                    <div className="flex justify-between">
                      <span className="inline-flex items-center">
                        <span className="border border-gray-blue-200 rounded-xl text-sm bg-transparent text-gray-blue-700 px-2">
                          {item?.apiNameLabel}
                        </span>
                        <a
                          href={item?.record_url}
                          target="_blank"
                          onClick={() => {
                            captureEvent("ECRM_EditMatch_RedirectToSF", {
                              type: type,
                            });
                          }}
                        >
                          {" "}
                          <Image className="no-invert" src="/images/ext/crm/hyperlink.svg" />
                        </a>
                      </span>
                      {item?.match_confirmed ? (
                        <Image className="no-invert" src="/images/ext/crm/matched.svg" />
                      ) : (
                        <button
                          onClick={() => {
                            matchRecord(item?.apiName, item?.Id);
                            captureEvent("ECRM_EditMatch_ChooseMatchButton", {
                              type: type,
                            });
                          }}
                          className="inline-flex justify-center items-center whitespace-nowrap text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border bg-background border-brand-300 text-brand-700 px-4 py-2 h-8 rounded-[8px] font-semibold"
                        >
                          Match
                        </button>
                      )}
                    </div>
                    <div className="mt-3">
                      {crmRecordKeyValue(type, item?.apiNameLabel, crmObjectsConfig) &&
                        crmRecordKeyValue(type, item?.apiNameLabel, crmObjectsConfig).length > 0 &&
                        crmRecordKeyValue(type, item?.apiNameLabel, crmObjectsConfig).map((el) => {
                          if (el.system_field_name !== "location") {
                            return (
                              <div className="mb-3">
                                {el.system_field_name !== "crm_owner_name" && (
                                  <RecordRow
                                    label={el?.label}
                                    value={
                                      el?.crm_field_api_name
                                        ? el.system_field_name === "company" &&
                                          item?.crm_resolved_company_name
                                          ? item?.crm_resolved_company_name
                                          : item?.data?.[el.system_field_name]?.value
                                        : ""
                                    }
                                  />
                                )}
                                {el.system_field_name === "crm_owner_name" && (
                                  <RecordRow label={el?.label} value={item?.crm_owner_name} />
                                )}
                              </div>
                            );
                          }
                          if (el.system_field_name === "location") {
                            return (
                              <div className="mb-3">
                                <RecordRow
                                  label={el?.label}
                                  value={null}
                                  location={true}
                                  locationData={
                                    type === "company"
                                      ? {
                                          city: item?.data?.headquarters_city?.value,
                                          state: item?.data?.headquarters_state?.value,
                                          country: item?.data?.headquarters_country?.value,
                                        }
                                      : {
                                          city: item?.data?.city?.value,
                                          state: item?.data?.state?.value,
                                          country: item?.data?.country?.value,
                                        }
                                  }
                                />
                              </div>
                            );
                          }
                        })}
                    </div>
                  </div>
                ))
              ) : (
                <>
                  {((type === "company" &&
                    Array.isArray(companyWithWritePermission) &&
                    companyWithWritePermission.length > 0) ||
                    (Array.isArray(personsWithWritePermission) && personsWithWritePermission.length > 0)) && (
                    <button
                      onClick={() => {
                        setCreate(true);
                        captureEvent("ECRM_EditMatch_CreateNewBtn", {
                          type: type,
                        });
                      }}
                      className="mb-4 flex w-full justify-center items-center whitespace-nowrap text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border bg-background border-brand-300 text-brand-700 px-4 py-2 h-9 rounded-[8px] font-semibold"
                    >
                      Create a new record
                    </button>
                  )}
                  <NoInfo
                    heading="We couldn’t find any matches"
                    description="Edit the search criteria and try again"
                    imgWidth="w-6/12"
                  />
                </>
              )}
            </>
          )}

          {allRecords.length > 0 && (
            <>
              {((type === "company" &&
                Array.isArray(companyWithWritePermission) &&
                companyWithWritePermission.length > 0) ||
                (Array.isArray(personsWithWritePermission) && personsWithWritePermission.length > 0)) && (
                <button
                  onClick={() => {
                    setCreate(true);
                    captureEvent("ECRM_EditMatch_CreateNewBtn", {
                      type: type,
                    });
                  }}
                  className="mt-4 flex w-full justify-center items-center whitespace-nowrap text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border bg-background border-brand-300 text-brand-700 px-4 py-2 h-9 rounded-[8px] font-semibold"
                >
                  Create a new record
                </button>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}
